import type { LogEntry } from '@/types/log'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { repairApi } from '../api/repairApi'
import type { Repair, RepairDetail } from '../types/repair'

const invalidateRepairQueries = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: ['repairs'] })
  queryClient.invalidateQueries({ queryKey: ['repair'] })
  queryClient.invalidateQueries({ queryKey: ['repair-logs'] })
}

export const useGetRepairs = (params?: { status?: string; departmentId?: string; itemId?: string }, enabled = true) => {
  return useQuery<Repair[]>({
    queryKey: ['repairs', params],
    queryFn: () => repairApi.getRepairs(params),
    enabled,
  })
}

export const useGetRepair = (id?: string | null, enabled = true) => {
  return useQuery<RepairDetail>({
    queryKey: ['repair', id],
    queryFn: () => repairApi.getRepair(id!),
    enabled: enabled && !!id,
  })
}

export const useGetRepairLogs = (id?: string | null, enabled = true) => {
  return useQuery<LogEntry[]>({
    queryKey: ['repair-logs', id],
    queryFn: () => repairApi.getRepairLogs(id!),
    enabled: enabled && !!id,
  })
}

export const useCreateRepair = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: repairApi.createRepair,
    onSuccess: (_data, variables) => {
      invalidateRepairQueries(queryClient)
      if (variables.itemId) queryClient.invalidateQueries({ queryKey: ['item', variables.itemId] })
    },
  })
}

export const useAcknowledgeRepair = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id }: { id: string }) => repairApi.acknowledgeRepair(id),
    onSuccess: () => {
      invalidateRepairQueries(queryClient)
    },
  })
}

export const useUpdateRepairStatus = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Parameters<typeof repairApi.updateRepairStatus>[1] }) =>
      repairApi.updateRepairStatus(id, payload),
    onSuccess: () => {
      invalidateRepairQueries(queryClient)
    },
  })
}

export const useCompleteRepair = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload?: Parameters<typeof repairApi.completeRepair>[1] }) =>
      repairApi.completeRepair(id, payload),
    onSuccess: () => {
      invalidateRepairQueries(queryClient)
    },
  })
}

export const useAssignRepair = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: { technicianId: string } }) =>
      repairApi.assignRepair(id, payload),
    onSuccess: () => {
      invalidateRepairQueries(queryClient)
    },
  })
}
